import React, { FC } from 'react';
import { Chip, Box, Grid, Tooltip } from '@mui/material';
import {
  ReadingsRequestDto,
  ReadingsRequestSensorDto,
  ReadingsRequestSensorTypeDto,
} from '@thingslog/repositories';
import { SensorDataDeviceGroupOptions, SensorDataAllDevicesOption } from '../../models';
import { SensorNameSnapshotter } from '../../utils/SensorNameSnapshotter';
import { useTranslation } from 'react-i18next';

interface SelectedItemsDisplayProps {
  payload: ReadingsRequestDto;
  onPayloadChange: (payload: ReadingsRequestDto) => void;
  deviceGroupOptions: SensorDataDeviceGroupOptions[];
  deviceOptions: SensorDataAllDevicesOption[];
  sensorNameSnapshotter: SensorNameSnapshotter;
}

export const SelectedItemsDisplay: FC<SelectedItemsDisplayProps> = ({
  payload,
  onPayloadChange,
  deviceGroupOptions,
  deviceOptions,
  sensorNameSnapshotter,
}: SelectedItemsDisplayProps) => {
  const { t } = useTranslation();
  const handleRemoveDeviceGroup = (deviceGroupId: number): void => {
    onPayloadChange({
      ...payload,
      deviceGroupIds: payload.deviceGroupIds.filter((id: number) => id !== deviceGroupId),
    });
  };

  const handleRemoveDeviceSensor = (deviceNumber: string, sensorIndex: number): void => {
    onPayloadChange({
      ...payload,
      deviceSensorList: payload.deviceSensorList.filter(
        (sensor: ReadingsRequestSensorDto) =>
          !(sensor.deviceNumber === deviceNumber && sensor.sensorIndex === sensorIndex)
      ),
    });
  };

  const handleRemoveDeviceSensorType = (index: number): void => {
    onPayloadChange({
      ...payload,
      deviceSensorTypeList: payload.deviceSensorTypeList.filter(
        (_: ReadingsRequestSensorTypeDto, i: number) => i !== index
      ),
    });
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {/* Device Groups */}
        {payload.deviceGroupIds.map((deviceGroupId: number) => {
          const deviceGroup = deviceGroupOptions.find(
            (option: SensorDataDeviceGroupOptions) => option.deviceGroupId === deviceGroupId
          );
          return (
            <Chip
              key={deviceGroupId}
              label={deviceGroup?.deviceGroupName || `${t('sensors_data_group')} ${deviceGroupId}`}
              onDelete={(): void => handleRemoveDeviceGroup(deviceGroupId)}
              size="medium"
              color="primary"
              variant="outlined"
            />
          );
        })}

        {/* Device-Sensor Pairs */}
        {payload.deviceSensorList.map((sensor: ReadingsRequestSensorDto, index: number) => {
          const device = deviceOptions.find(
            (option: SensorDataAllDevicesOption) => option.deviceNumber === sensor.deviceNumber
          );
          const sensorName = sensorNameSnapshotter.getSensorName(
            sensor.deviceNumber,
            sensor.sensorIndex
          );
          const label = device
            ? sensorName
              ? `${device.deviceName} - ${sensorName} (${sensor.sensorIndex})`
              : `${device.deviceName} - ${sensor.sensorIndex}`
            : sensorName
            ? `${sensor.deviceNumber} - ${sensorName} (${sensor.sensorIndex})`
            : `${sensor.deviceNumber} - ${sensor.sensorIndex}`;
          return (
            <Chip
              key={`${sensor.deviceNumber}-${sensor.sensorIndex}-${index}`}
              label={label}
              onDelete={(): void =>
                handleRemoveDeviceSensor(sensor.deviceNumber, sensor.sensorIndex)
              }
              size="medium"
              color="warning"
              variant="outlined"
            />
          );
        })}

        {/* Device-Sensor Types */}
        {payload.deviceSensorTypeList.map(
          (deviceSensorType: ReadingsRequestSensorTypeDto, index: number) => {
            const deviceNames = deviceSensorType.deviceNumbers
              .map((deviceNumber: string) => {
                const device = deviceOptions.find(
                  (option: SensorDataAllDevicesOption) => option.deviceNumber === deviceNumber
                );
                return device?.deviceName || deviceNumber;
              })
              .join(', ');
            const deviceCount = deviceSensorType.deviceNumbers.length;
            const label = `${deviceSensorType.sensorType} - ${deviceCount}`;
            return (
              <Tooltip key={`${deviceSensorType.sensorType}-${index}`} title={deviceNames}>
                <Chip
                  label={label}
                  onDelete={(): void => handleRemoveDeviceSensorType(index)}
                  size="medium"
                  color="success"
                  variant="outlined"
                />
              </Tooltip>
            );
          }
        )}
      </Box>
    </Grid>
  );
};
